import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, Briefcase, Building2, HeartHandshake, Landmark, Quote, Scale, ScrollText, ShieldCheck, Star } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { DATE_LOCALES } from "@/i18n";
import { Trans, useTranslation } from "react-i18next";

const ICONS = { Briefcase, Scale, HeartHandshake, Building2, ShieldCheck, ScrollText };

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Home() {
  const { t, i18n } = useTranslation();
  const { profile } = useOutletContext() || {};
  const [blogs, setBlogs] = useState([]);
  const dateLoc = DATE_LOCALES[i18n.language?.slice(0, 2)] || "en-IN";

  useEffect(() => {
    api.get("/public/blogs?limit=3").then(({ data }) => setBlogs(data.items)).catch(() => {});
  }, [i18n.language]);

  if (!profile) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-32 space-y-6" data-testid="home-skeleton">
        <div className="h-10 w-2/3 bg-muted animate-pulse" />
        <div className="h-6 w-1/2 bg-muted animate-pulse" />
        <div className="h-72 bg-muted animate-pulse" />
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="home-hero" className="bg-navy text-ivory relative overflow-hidden hero-grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center relative z-10">
          <div>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-gold inline-block" /> {profile.firm_name} — {t("common.est")}
            </motion.p>
            <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="font-serif text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.05] tracking-tight">
              {profile.tagline}
            </motion.h1>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
              className="mt-6 text-ivory/70 text-base sm:text-lg leading-relaxed max-w-xl">
              {profile.about_short}
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-10 flex flex-wrap gap-4">
              <Link to="/book-consultation" data-testid="hero-book-consultation-btn"
                className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-gold-600 transition-colors duration-300 font-semibold uppercase tracking-wider text-sm px-8 py-4">
                {t("home.bookConsultation")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/practice-areas" data-testid="hero-practice-areas-btn"
                className="inline-flex items-center gap-2 border-2 border-ivory/40 text-ivory hover:border-gold hover:text-gold transition-colors duration-300 font-semibold uppercase tracking-wider text-sm px-8 py-4">
                {t("home.practiceAreas")}
              </Link>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-12 flex items-center gap-4">
              <div className="h-14 w-14 border-2 border-gold p-0.5">
                <img src={profile.photo_url} alt={profile.name} className="h-full w-full object-cover" />
              </div>
              <div>
                <p className="font-serif text-lg font-semibold">{profile.name}</p>
                <p className="text-xs uppercase tracking-widest text-ivory/60">{profile.title} · {t("common.years", { count: profile.years_experience })}</p>
              </div>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative hidden lg:block">
            <div className="absolute -top-4 -left-4 w-full h-full border-2 border-gold/60" />
            <img src={profile.photo_url} alt={profile.name} data-testid="hero-lawyer-photo"
              className="relative w-full h-[560px] object-cover object-top shadow-2xl" />
            <div className="absolute -bottom-6 -right-6 bg-ivory text-navy p-6 shadow-hovercard max-w-xs">
              <div className="flex items-center gap-1 text-gold mb-2">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-gold" />)}
              </div>
              <p className="font-serif text-3xl font-semibold">{profile.stats?.success_rate}</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{t("home.successAcross", { count: profile.stats?.cases_handled })}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats bar */}
      <section data-testid="home-stats" className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            [profile.stats?.cases_handled, t("home.casesHandled")],
            [profile.stats?.success_rate, t("home.successRate")],
            [profile.stats?.clients_served, t("home.clientsServed")],
            [profile.stats?.settlements_recovered, t("home.recovered")],
          ].map(([value, label], i) => (
            <motion.div key={label} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="text-center lg:text-left">
              <p className="font-serif text-4xl sm:text-5xl font-semibold text-navy">{value}</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-2">{label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Practice areas */}
      <section data-testid="home-practice-areas" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-700 mb-3">{t("home.whatWeDo")}</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-semibold text-navy tracking-tight">{t("home.practiceAreas")}</h2>
            </div>
            <Link to="/practice-areas" data-testid="practice-areas-view-all"
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-navy hover:text-gold-700 transition-colors">
              {t("common.viewAll")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(profile.practice_areas || []).map((area, i) => {
              const Icon = ICONS[area.icon] || Scale;
              return (
                <motion.div key={area.id} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 3}>
                  <Link to="/practice-areas" data-testid={`practice-area-card-${area.id}`}
                    className="group block bg-white border border-border p-8 h-full transition-all duration-300 hover:shadow-hovercard hover:border-gold hover:-translate-y-1">
                    <Icon className="h-8 w-8 text-gold-700 mb-6" />
                    <h3 className="font-serif text-2xl font-semibold text-navy mb-3">{area.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{area.description}</p>
                    <span className="inline-flex items-center gap-2 mt-6 text-xs font-semibold uppercase tracking-wider text-navy group-hover:text-gold-700 transition-colors">
                      {t("common.learnMore")} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About */}
      <section data-testid="home-about" className="py-24 sm:py-32 bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={profile.photo_alt_url || profile.photo_url} alt={profile.name}
              className="w-4/5 h-[480px] object-cover object-top shadow-card" />
            <img src={profile.hero_image} alt="Courthouse"
              className="absolute -bottom-10 -right-2 sm:right-0 w-1/2 h-64 object-cover border-8 border-ivory shadow-hovercard" />
            <div className="absolute -top-6 -left-6 bg-navy text-ivory p-6">
              <p className="font-serif text-4xl font-semibold text-gold">{profile.years_experience}</p>
              <p className="text-xs uppercase tracking-widest">{t("home.yearsPractice")}</p>
            </div>
          </div>
          <div className="lg:pl-6">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-700 mb-3">{t("home.aboutFirm")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-semibold text-navy tracking-tight mb-6">
              {t("home.meet", { name: profile.name })}
            </h2>
            <Quote className="h-8 w-8 text-gold mb-4" />
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
              {profile.about_short}
            </p>
            <ul className="space-y-3 mb-10">
              {(profile.qualifications || []).slice(0, 3).map((q) => (
                <li key={q} className="flex items-start gap-3 text-sm text-foreground">
                  <span className="h-1.5 w-1.5 bg-gold mt-2 shrink-0" /> {q}
                </li>
              ))}
            </ul>
            <Link to="/attorney" data-testid="about-full-profile-btn"
              className="inline-flex items-center gap-2 bg-navy text-ivory hover:bg-navy-800 transition-colors duration-300 font-semibold uppercase tracking-wider text-sm px-8 py-4">
              {t("home.fullProfile")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section data-testid="home-achievements" className="py-24 sm:py-32 bg-navy text-ivory hero-grain relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-3 gap-12">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-3">{t("home.recognition")}</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight">{t("home.awards")}</h2>
              <p className="mt-5 text-ivory/60 text-sm leading-relaxed">
                {t("home.awardsBlurb")}
              </p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-px bg-ivory/10">
              {(profile.awards || []).map((a, i) => (
                <motion.div key={a.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                  className="bg-navy p-8 border-l-2 border-transparent hover:border-gold transition-colors duration-300">
                  <Award className="h-6 w-6 text-gold mb-4" />
                  <h3 className="font-serif text-xl font-semibold">{a.title}</h3>
                  <p className="text-xs uppercase tracking-widest text-ivory/50 mt-2">{a.year}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="home-testimonials" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-left mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-700 mb-3">{t("home.clientVoices")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-semibold text-navy tracking-tight">{t("home.whatClientsSay")}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {(profile.testimonials || []).map((t, i) => (
              <motion.figure key={t.name} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 2}
                data-testid={`testimonial-${i}`}
                className="bg-white border border-border p-8 sm:p-10 relative">
                <Quote className="h-7 w-7 text-gold mb-5" />
                <blockquote className="font-serif text-xl sm:text-2xl text-navy leading-snug">“{t.quote}”</blockquote>
                <figcaption className="mt-6 pt-6 border-t border-border">
                  <p className="font-semibold text-navy text-sm">{t.name}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest mt-1">{t.role}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Featured blogs */}
      <section data-testid="home-featured-blogs" className="py-24 sm:py-32 bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-700 mb-3">{t("home.insights")}</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-semibold text-navy tracking-tight">{t("home.fromTheBrief")}</h2>
            </div>
            <Link to="/blog" data-testid="featured-blogs-view-all"
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-navy hover:text-gold-700 transition-colors">
              {t("home.allArticles")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {blogs.map((post, i) => (
              <motion.article key={post.id} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}>
                <Link to={`/blog/${post.slug}`} data-testid={`featured-blog-${post.slug}`} className="group block">
                  <div className="overflow-hidden mb-5">
                    <img src={post.cover_image} alt={post.title}
                      className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-gold-700">{post.category} · {t("common.minRead", { count: post.read_time })}</p>
                  <h3 className="font-serif text-2xl font-semibold text-navy mt-2 leading-snug group-hover:text-gold-700 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{post.excerpt}</p>
                  <p className="text-xs text-muted-foreground mt-4">{fmtDate(post.published_at, dateLoc)}</p>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-contact-cta" className="relative py-28 sm:py-36 bg-navy hero-grain overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-left sm:text-center relative z-10">
          <Landmark className="h-10 w-10 text-gold mb-6 sm:mx-auto" />
          <h2 className="font-serif text-4xl sm:text-6xl font-semibold text-ivory tracking-tight leading-tight">
            <Trans i18nKey="home.ctaTitle" components={{ italic: <span className="text-gold italic" /> }} />
          </h2>
          <p className="mt-6 text-ivory/70 max-w-2xl sm:mx-auto">
            {t("home.ctaBody")}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 sm:justify-center">
            <Link to="/book-consultation" data-testid="cta-book-consultation-btn"
              className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-gold-600 transition-colors duration-300 font-semibold uppercase tracking-wider text-sm px-8 py-4">
              {t("home.bookConsultation")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="cta-contact-btn"
              className="inline-flex items-center gap-2 border-2 border-ivory/40 text-ivory hover:border-gold hover:text-gold transition-colors duration-300 font-semibold uppercase tracking-wider text-sm px-8 py-4">
              {t("home.contactOffice")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
